// src/contexts/ChatContext.js
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { db, auth } from '../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import {
    collection,
    doc,
    addDoc,
    updateDoc,
    deleteDoc,
    onSnapshot,
    query,
    where,
    orderBy,
    limit,
    getDocs,
    getDoc,
    setDoc,
    Timestamp,
    serverTimestamp,
    arrayUnion,
    arrayRemove,
    writeBatch
} from 'firebase/firestore';
import presenceService from '../services/PresenceService';
import { requestNotificationPermission, sendPushNotification } from '../services/notificationService';

const ChatContext = createContext();

export const useChat = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
};

export const ChatProvider = ({ children }) => {
    // Auth state
    const [currentUser, setCurrentUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);

    // Chat states
    const [channels, setChannels] = useState([]);
    const [activeChannel, setActiveChannel] = useState(null);
    const [messages, setMessages] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [presence, setPresence] = useState({});
    const [typingUsers, setTypingUsers] = useState({});
    const [unreadCounts, setUnreadCounts] = useState({});
    const [error, setError] = useState(null);
    const [isTyping, setIsTyping] = useState(false);

    // Message status tracking
    const [messageStatuses, setMessageStatuses] = useState({});
    const messageStatusListenersRef = useRef({});

    // Chat notification states
    const [chatNotifications, setChatNotifications] = useState([]);
    const [chatUnreadCount, setChatUnreadCount] = useState(0);

    // Refs for unsubscribing
    const messagesUnsubscribeRef = useRef(null);
    const channelsUnsubscribeRef = useRef(null);
    const typingUnsubscribeRef = useRef(null);
    const presenceUnsubscribeRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const notificationInitRef = useRef(false);
    const isProcessingMessagesRef = useRef(false);
    const hasMarkedMessagesAsReadRef = useRef(false);
    const hasUserViewedChatRef = useRef(false);

    // ============ AUTHENTICATION ============
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            try {
                if (user) {
                    const userDoc = await getDoc(doc(db, 'users', user.uid));

                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        setCurrentUser({
                            uid: user.uid,
                            email: user.email,
                            emailVerified: user.emailVerified,
                            displayName: user.displayName || userData.nome || user.email,
                            ...userData,
                            id: user.uid
                        });
                    } else {
                        const defaultUserData = {
                            nome: user.displayName || user.email?.split('@')[0] || 'Usuário',
                            email: user.email,
                            role: 'Operacional',
                            createdAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString(),
                            status: 'active'
                        };

                        await setDoc(doc(db, 'users', user.uid), defaultUserData);

                        setCurrentUser({
                            uid: user.uid,
                            email: user.email,
                            emailVerified: user.emailVerified,
                            displayName: user.displayName || defaultUserData.nome,
                            ...defaultUserData,
                            id: user.uid
                        });
                    }
                } else {
                    setCurrentUser(null);
                }
            } catch (error) {
                console.error('Error fetching user profile:', error);
                if (user) {
                    setCurrentUser({
                        uid: user.uid,
                        email: user.email || 'unknown@email.com',
                        nome: user.displayName || user.email?.split('@')[0] || 'Usuário',
                        role: 'Operacional',
                        emailVerified: user.emailVerified || false,
                        displayName: user.displayName || '',
                        id: user.uid
                    });
                } else {
                    setCurrentUser(null);
                }
            } finally {
                setAuthLoading(false);
            }
        });

        return () => unsubscribe();
    }, []);

    // ============ NOTIFICATIONS ============
    useEffect(() => {
        if (!currentUser || notificationInitRef.current || authLoading) return;
        notificationInitRef.current = true;

        const initNotifications = async () => {
            const granted = await requestNotificationPermission();
            if (granted) {
                console.log('🔔 Notification permission granted');
            }
        };
        initNotifications();
    }, [currentUser, authLoading]);

    // ============ SAVE CHAT NOTIFICATIONS ============
    const saveChatNotifications = useCallback((notification) => {
        if (!currentUser || !notification) return;
        try {
            const saved = localStorage.getItem(`chat_notifications_${currentUser.uid}`);
            let notifications = saved ? JSON.parse(saved) : [];
            notifications = [notification, ...notifications.filter(n => n.id !== notification.id)];
            notifications = notifications.slice(0, 100);
            localStorage.setItem(`chat_notifications_${currentUser.uid}`, JSON.stringify(notifications));
        } catch (e) {
            console.error('Error saving chat notifications:', e);
        }
    }, [currentUser]);

    const loadChatNotifications = useCallback(() => {
        if (!currentUser) return;
        try {
            const saved = localStorage.getItem(`chat_notifications_${currentUser.uid}`);
            if (saved) {
                const parsed = JSON.parse(saved);
                const oneWeekAgo = new Date();
                oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                const filtered = parsed.filter(n => new Date(n.timestamp) > oneWeekAgo);
                setChatNotifications(filtered);
                setChatUnreadCount(filtered.filter(n => !n.read).length);
            }
        } catch (e) {
            console.error('Error loading chat notifications:', e);
        }
    }, [currentUser]);

    useEffect(() => {
        if (currentUser) {
            loadChatNotifications();
        }
    }, [currentUser, loadChatNotifications]);

    // ============ PRESENCE SERVICE ============
    useEffect(() => {
        if (!currentUser || authLoading) return;

        presenceService.initialize(currentUser);

        const unsubscribePresence = presenceService.subscribeToOnlineUsers((onlineUsers) => {
            const presenceMap = {};
            onlineUsers.forEach(user => {
                presenceMap[user.id] = {
                    ...user,
                    isOnline: user.online
                };
            });
            setPresence(presenceMap);
        });

        presenceUnsubscribeRef.current = unsubscribePresence;

        return () => {
            if (presenceUnsubscribeRef.current) {
                presenceUnsubscribeRef.current();
            }
            presenceService.cleanup();
        };
    }, [currentUser, authLoading]);

    // ============ TYPING INDICATORS ============
    useEffect(() => {
        if (!activeChannel || !currentUser || authLoading) return;

        if (typingUnsubscribeRef.current) {
            typingUnsubscribeRef.current();
            typingUnsubscribeRef.current = null;
        }

        const unsubscribe = presenceService.subscribeToTyping(
            activeChannel.id,
            (typingList) => {
                setTypingUsers(prev => ({
                    ...prev,
                    [activeChannel.id]: typingList.filter(u => u.uid !== currentUser.uid)
                }));
            }
        );

        typingUnsubscribeRef.current = unsubscribe;

        return () => {
            if (typingUnsubscribeRef.current) {
                typingUnsubscribeRef.current();
                typingUnsubscribeRef.current = null;
            }
        };
    }, [activeChannel, currentUser, authLoading]);

    // ============ FETCH CHANNELS ============
    useEffect(() => {
        if (!currentUser || authLoading) return;

        const channelsRef = collection(db, 'chat_channels');
        const q = query(channelsRef, where('members', 'array-contains', currentUser.uid));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const channelList = [];
            snapshot.forEach((doc) => {
                const data = doc.data();
                const isPrivate = data.isPrivate || data.members?.length === 2;
                channelList.push({
                    id: doc.id,
                    ...data,
                    isPrivate
                });
            });

            channelList.sort((a, b) => {
                const aTime = a.lastMessageTime?.toMillis?.() || a.createdAt?.toMillis?.() || 0;
                const bTime = b.lastMessageTime?.toMillis?.() || b.createdAt?.toMillis?.() || 0;
                return bTime - aTime;
            });

            setChannels(channelList);
            setLoading(false);
        }, (err) => {
            console.error('Error fetching channels:', err);
            setError(err.message);
            setLoading(false);
        });

        channelsUnsubscribeRef.current = unsubscribe;

        return () => {
            if (channelsUnsubscribeRef.current) {
                channelsUnsubscribeRef.current();
            }
        };
    }, [currentUser, authLoading]);

    // ============ GET USER ============
    const getUser = useCallback((userId) => {
        return users.find(u => u.uid === userId);
    }, [users]);

    // ============ NOTIFY NEW MESSAGE (DEFINIDA ANTES DE SER USADA) ============
    const notifyNewMessage = useCallback(async (channelId, message, senderName) => {
        if (!currentUser) return;
        if (message.senderId === currentUser.uid) return;

        const channel = channels.find(c => c.id === channelId);
        if (!channel) return;

        const channelName = channel.isPrivate
            ? getUser(channel.members?.find(id => id !== currentUser.uid))?.nome || 'User'
            : channel.name || 'Group Chat';

        const notification = {
            id: `chat_${message.id || Date.now()}_${Date.now()}`,
            type: 'chat_message',
            channelId: channelId,
            channelName: channelName,
            senderId: message.senderId,
            senderName: senderName || message.senderName || 'Unknown User',
            message: message.text || 'New message',
            timestamp: new Date().toISOString(),
            read: false,
            module: 'chat',
            isChatNotification: true,
            needsChannelId: true,
            actionType: 'open_chat'
        };

        setChatNotifications(prev => {
            const exists = prev.some(n => n.id === notification.id);
            if (exists) return prev;
            const newNotifications = [notification, ...prev];
            const unread = newNotifications.filter(n => !n.read).length;
            setChatUnreadCount(unread);
            return newNotifications;
        });

        saveChatNotifications(notification);

        // Enviar notificação push APENAS se a aba NÃO estiver visível
        if (document.visibilityState !== 'visible') {
            try {
                await sendPushNotification(
                    `${senderName || message.senderName || 'Someone'} in ${channelName}`,
                    message.text || 'New message',
                    { channelId, messageId: message.id }
                );
            } catch (err) {
                console.log('Browser notification not supported or blocked');
            }
        }
    }, [currentUser, channels, getUser, saveChatNotifications]);

    // ============ MARK MESSAGES AS READ ============
    const markMessagesAsRead = useCallback(async (channelId, messageList, force = false) => {
        if (!currentUser || !channelId || !messageList || messageList.length === 0) return;
        if (isProcessingMessagesRef.current) return;

        if (!force && !hasUserViewedChatRef.current) {
            console.log('📬 Skipping auto-mark as read - user has not viewed chat yet');
            return;
        }

        try {
            isProcessingMessagesRef.current = true;

            const unreadMessages = messageList.filter(
                msg => msg.senderId !== currentUser.uid &&
                    (!msg.readBy || !msg.readBy.includes(currentUser.uid))
            );

            if (unreadMessages.length === 0) {
                isProcessingMessagesRef.current = false;
                return;
            }

            const batch = writeBatch(db);
            unreadMessages.forEach(msg => {
                const msgRef = doc(db, 'chat_channels', channelId, 'messages', msg.id);
                batch.update(msgRef, {
                    readBy: arrayUnion(currentUser.uid),
                    status: 'read'
                });
            });
            await batch.commit();

            setMessageStatuses(prev => {
                const updates = {};
                unreadMessages.forEach(msg => {
                    updates[msg.id] = 'read';
                });
                return { ...prev, ...updates };
            });

            console.log(`📬 Marked ${unreadMessages.length} messages as read in channel ${channelId}`);
            hasMarkedMessagesAsReadRef.current = true;
        } catch (err) {
            console.error('Error marking messages as read:', err);
        } finally {
            isProcessingMessagesRef.current = false;
        }
    }, [currentUser]);

    // ============ MARK MESSAGES AS READ (MANUAL) ============
    const markMessagesAsReadManually = useCallback(async (channelId) => {
        if (!currentUser || !channelId) return;
        hasUserViewedChatRef.current = true;
        await markMessagesAsRead(channelId, messages, true);
    }, [currentUser, messages, markMessagesAsRead]);

    // ============ FETCH MESSAGES (COM NOTIFICAÇÕES) ============
    useEffect(() => {
        if (!activeChannel || !currentUser || authLoading) {
            setMessages([]);
            return;
        }

        hasUserViewedChatRef.current = false;
        hasMarkedMessagesAsReadRef.current = false;

        if (messagesUnsubscribeRef.current) {
            messagesUnsubscribeRef.current();
            messagesUnsubscribeRef.current = null;
        }

        const messagesRef = collection(db, 'chat_channels', activeChannel.id, 'messages');
        const q = query(messagesRef, orderBy('timestamp', 'asc'), limit(500));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const messageList = [];
            const statusUpdates = {};

            snapshot.forEach((doc) => {
                const data = doc.data();
                const message = {
                    id: doc.id,
                    ...data,
                    timestamp: data.timestamp?.toDate?.() || new Date()
                };
                messageList.push(message);

                if (data.status) {
                    statusUpdates[doc.id] = data.status;
                }
            });

            setMessages(messageList);

            // ============================================
            // NOTIFICAR SOBRE NOVAS MENSAGENS DE OUTROS USUÁRIOS
            // ============================================
            if (currentUser && activeChannel && messageList.length > 0) {
                messageList.forEach(msg => {
                    if (msg.senderId !== currentUser.uid &&
                        msg.timestamp &&
                        !msg._notified) {

                        msg._notified = true;

                        const sender = users.find(u => u.uid === msg.senderId);
                        const senderName = sender?.nome || msg.senderName || 'Alguém';

                        // notifyNewMessage já está definida neste ponto
                        notifyNewMessage(activeChannel.id, msg, senderName);
                    }
                });
            }

            if (Object.keys(statusUpdates).length > 0) {
                setMessageStatuses(prev => ({
                    ...prev,
                    ...statusUpdates
                }));
            }

            // Não marcar automaticamente - aguardar interação do usuário
        }, (err) => {
            console.error('Error fetching messages:', err);
            setError(err.message);
        });

        messagesUnsubscribeRef.current = unsubscribe;

        return () => {
            if (messagesUnsubscribeRef.current) {
                messagesUnsubscribeRef.current();
                messagesUnsubscribeRef.current = null;
            }
        };
    }, [activeChannel, currentUser, authLoading, users, notifyNewMessage]);

    // ============ FETCH USERS ============
    useEffect(() => {
        if (!currentUser || authLoading) return;

        const usersRef = collection(db, 'users');
        const unsubscribe = onSnapshot(usersRef, (snapshot) => {
            const userList = [];
            snapshot.forEach((doc) => {
                const data = doc.data();
                userList.push({
                    uid: doc.id,
                    ...data,
                    displayName: data.nome || data.email
                });
            });
            setUsers(userList);
        });

        return () => unsubscribe();
    }, [currentUser, authLoading]);

    // ============ MARK CHANNEL AS READ ============
    const markChannelAsRead = async (channelId) => {
        if (!currentUser) return;

        try {
            const channelRef = doc(db, 'chat_channels', channelId);
            await updateDoc(channelRef, {
                readBy: arrayUnion(currentUser.uid)
            });
        } catch (err) {
            console.error('Error marking channel as read:', err);
        }
    };

    // ============ MARK CHANNEL NOTIFICATIONS READ ============
    const markChannelNotificationsRead = useCallback((channelId) => {
        if (!channelId) return;
        setChatNotifications(prev => {
            const updated = prev.map(n =>
                n.channelId === channelId ? { ...n, read: true } : n
            );
            const unreadCount = updated.filter(n => !n.read).length;
            setChatUnreadCount(unreadCount);

            if (currentUser) {
                try {
                    localStorage.setItem(`chat_notifications_${currentUser.uid}`, JSON.stringify(updated));
                } catch (e) {
                    console.error('Error saving notifications:', e);
                }
            }

            return updated;
        });
    }, [currentUser]);

    // ============ SEND MESSAGE ============
    const sendMessage = async (channelId, text, attachments = [], replyTo = null) => {
        if (!currentUser || (!text.trim() && attachments.length === 0)) return;

        try {
            const messagesRef = collection(db, 'chat_channels', channelId, 'messages');

            const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            setMessageStatuses(prev => ({
                ...prev,
                [tempId]: 'sending'
            }));

            const messageData = {
                text: text.trim(),
                senderId: currentUser.uid,
                senderName: currentUser.nome || currentUser.email,
                senderRole: currentUser.role,
                timestamp: serverTimestamp(),
                attachments: attachments || [],
                replyTo: replyTo,
                readBy: [currentUser.uid],
                edited: false,
                status: 'sent'
            };

            const docRef = await addDoc(messagesRef, messageData);

            const channel = channels.find(c => c.id === channelId);
            const otherMembers = channel?.members?.filter(id => id !== currentUser.uid) || [];

            setMessageStatuses(prev => {
                const newStatuses = { ...prev };
                delete newStatuses[tempId];
                newStatuses[docRef.id] = 'sent';
                return newStatuses;
            });

            const channelRef = doc(db, 'chat_channels', channelId);
            await updateDoc(channelRef, {
                lastMessage: text.trim(),
                lastMessageTime: serverTimestamp(),
                lastMessageSender: currentUser.uid,
                lastMessageSenderName: currentUser.nome || currentUser.email
            });

            setIsTyping(false);
            await presenceService.setTyping(channelId, false);

            const onlineMembers = otherMembers.filter(id => presence[id]?.isOnline);

            if (onlineMembers.length > 0) {
                setTimeout(async () => {
                    try {
                        const msgRef = doc(db, 'chat_channels', channelId, 'messages', docRef.id);
                        await updateDoc(msgRef, {
                            status: 'delivered'
                        });
                        setMessageStatuses(prev => ({
                            ...prev,
                            [docRef.id]: 'delivered'
                        }));
                    } catch (err) {
                        console.error('Error updating message status to delivered:', err);
                    }
                }, 1000);
            }

            return { id: docRef.id, ...messageData, status: 'sent' };
        } catch (err) {
            console.error('Error sending message:', err);
            setError(err.message);
            throw err;
        }
    };

    // ============ SET TYPING ============
    const setTyping = useCallback(async (channelId, isTyping) => {
        if (!currentUser || !channelId) return;

        setIsTyping(isTyping);

        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }

        if (isTyping) {
            await presenceService.setTyping(channelId, true);

            typingTimeoutRef.current = setTimeout(async () => {
                setIsTyping(false);
                await presenceService.setTyping(channelId, false);
            }, 3000);
        } else {
            await presenceService.setTyping(channelId, false);
        }
    }, [currentUser]);

    // ============ CREATE CHANNEL ============
    const createChannel = async (name, members, isPrivate = true) => {
        if (!currentUser) return;

        try {
            const channelRef = collection(db, 'chat_channels');
            const memberIds = [currentUser.uid, ...members];

            const channelData = {
                name: name,
                isPrivate: isPrivate,
                members: memberIds,
                createdBy: currentUser.uid,
                createdByDisplayName: currentUser.nome || currentUser.email,
                createdAt: serverTimestamp(),
                readBy: [currentUser.uid],
                lastMessage: '',
                lastMessageTime: null,
                lastMessageSender: null,
                lastMessageSenderName: null,
                type: isPrivate ? 'private' : 'group'
            };

            const docRef = await addDoc(channelRef, channelData);

            const batch = writeBatch(db);
            memberIds.forEach(uid => {
                const userChannelRef = doc(db, 'users', uid, 'channels', docRef.id);
                batch.set(userChannelRef, {
                    channelId: docRef.id,
                    lastRead: null
                });
            });
            await batch.commit();

            return { id: docRef.id, ...channelData };
        } catch (err) {
            console.error('Error creating channel:', err);
            setError(err.message);
            throw err;
        }
    };

    // ============ ADD USER TO CHANNEL ============
    const addUserToChannel = async (channelId, userId) => {
        if (!currentUser) return;

        try {
            const channelRef = doc(db, 'chat_channels', channelId);
            await updateDoc(channelRef, {
                members: arrayUnion(userId)
            });

            const userChannelRef = doc(db, 'users', userId, 'channels', channelId);
            await setDoc(userChannelRef, {
                channelId: channelId,
                lastRead: null
            });
        } catch (err) {
            console.error('Error adding user to channel:', err);
            setError(err.message);
            throw err;
        }
    };

    // ============ REMOVE USER FROM CHANNEL ============
    const removeUserFromChannel = async (channelId, userId) => {
        if (!currentUser) return;

        try {
            const channelRef = doc(db, 'chat_channels', channelId);
            await updateDoc(channelRef, {
                members: arrayRemove(userId)
            });

            const userChannelRef = doc(db, 'users', userId, 'channels', channelId);
            await deleteDoc(userChannelRef);
        } catch (err) {
            console.error('Error removing user from channel:', err);
            setError(err.message);
            throw err;
        }
    };

    // ============ DELETE MESSAGE ============
    const deleteMessage = async (channelId, messageId) => {
        if (!currentUser) return;

        try {
            const messageRef = doc(db, 'chat_channels', channelId, 'messages', messageId);
            const messageDoc = await getDoc(messageRef);

            if (!messageDoc.exists()) {
                throw new Error('Message not found');
            }

            const messageData = messageDoc.data();
            if (messageData.senderId !== currentUser.uid) {
                throw new Error('You can only delete your own messages');
            }

            await deleteDoc(messageRef);

            setMessageStatuses(prev => {
                const newStatuses = { ...prev };
                delete newStatuses[messageId];
                return newStatuses;
            });
        } catch (err) {
            console.error('Error deleting message:', err);
            setError(err.message);
            throw err;
        }
    };

    // ============ EDIT MESSAGE ============
    const editMessage = async (channelId, messageId, newText) => {
        if (!currentUser || !newText.trim()) return;

        try {
            const messageRef = doc(db, 'chat_channels', channelId, 'messages', messageId);
            const messageDoc = await getDoc(messageRef);

            if (!messageDoc.exists()) {
                throw new Error('Message not found');
            }

            const messageData = messageDoc.data();
            if (messageData.senderId !== currentUser.uid) {
                throw new Error('You can only edit your own messages');
            }

            await updateDoc(messageRef, {
                text: newText.trim(),
                edited: true,
                editedAt: serverTimestamp()
            });
        } catch (err) {
            console.error('Error editing message:', err);
            setError(err.message);
            throw err;
        }
    };

    // ============ ADD REACTION ============
    const addReaction = async (channelId, messageId, emoji) => {
        if (!currentUser) return;
        try {
            const messageRef = doc(db, 'chat_channels', channelId, 'messages', messageId);
            await updateDoc(messageRef, {
                [`reactions.${emoji}`]: arrayUnion(currentUser.uid)
            });
        } catch (err) {
            console.error('Error adding reaction:', err);
            throw err;
        }
    };

    // ============ REMOVE REACTION ============
    const removeReaction = async (channelId, messageId, emoji) => {
        if (!currentUser) return;
        try {
            const messageRef = doc(db, 'chat_channels', channelId, 'messages', messageId);
            await updateDoc(messageRef, {
                [`reactions.${emoji}`]: arrayRemove(currentUser.uid)
            });
        } catch (err) {
            console.error('Error removing reaction:', err);
            throw err;
        }
    };

    // ============ HELPER FUNCTIONS ============
    const getUnreadCount = (channelId) => {
        return unreadCounts[channelId] || 0;
    };

    const getUserPresence = (userId) => {
        return presence[userId] || { status: 'offline', isOnline: false };
    };

    const getUsersWithPresence = () => {
        return users.map(user => ({
            ...user,
            presence: presence[user.uid] || { status: 'offline', isOnline: false }
        }));
    };

    const getOnlineUsers = () => {
        return users.filter(user => presence[user.uid]?.isOnline);
    };

    const isUserTyping = (channelId, userId) => {
        const typing = typingUsers[channelId] || [];
        return typing.some(u => u.uid === userId);
    };

    const getTypingUsers = (channelId) => {
        return typingUsers[channelId] || [];
    };

    const getTypingText = (channelId) => {
        const typing = typingUsers[channelId] || [];
        const filtered = typing.filter(u => u.uid !== currentUser?.uid);

        if (filtered.length === 0) return null;

        const names = filtered.map(u => u.displayName || 'User');

        if (names.length === 1) {
            return `${names[0]} is typing...`;
        } else if (names.length === 2) {
            return `${names[0]} and ${names[1]} are typing...`;
        } else {
            return `${names.length} people are typing...`;
        }
    };

    const updateMessageStatus = (messageId, status) => {
        setMessageStatuses(prev => ({
            ...prev,
            [messageId]: status
        }));
    };

    const getMessageStatus = (messageId) => {
        return messageStatuses[messageId] || 'sending';
    };

    const markChatNotificationRead = useCallback((notificationId) => {
        setChatNotifications(prev => {
            const updated = prev.map(n =>
                n.id === notificationId ? { ...n, read: true } : n
            );
            const unreadCount = updated.filter(n => !n.read).length;
            setChatUnreadCount(unreadCount);

            if (currentUser) {
                try {
                    localStorage.setItem(`chat_notifications_${currentUser.uid}`, JSON.stringify(updated));
                } catch (e) {
                    console.error('Error saving notifications:', e);
                }
            }

            return updated;
        });
    }, [currentUser]);

    const markAllChatNotificationsRead = useCallback(() => {
        setChatNotifications(prev => {
            const updated = prev.map(n => ({ ...n, read: true }));
            setChatUnreadCount(0);

            if (currentUser) {
                try {
                    localStorage.setItem(`chat_notifications_${currentUser.uid}`, JSON.stringify(updated));
                } catch (e) {
                    console.error('Error saving notifications:', e);
                }
            }

            return updated;
        });
    }, [currentUser]);

    const clearAllChatNotifications = useCallback(() => {
        setChatNotifications([]);
        setChatUnreadCount(0);
        if (currentUser) {
            localStorage.removeItem(`chat_notifications_${currentUser.uid}`);
        }
    }, [currentUser]);

    const getChannelUnreadCount = useCallback((channelId) => {
        if (!channelId) return 0;
        return chatNotifications.filter(n => n.channelId === channelId && !n.read).length;
    }, [chatNotifications]);

    // ============ LOADING STATE ============
    if (authLoading) {
        return <div>Loading authentication...</div>;
    }

    // ============ CONTEXT VALUE ============
    const value = {
        channels,
        activeChannel,
        setActiveChannel,
        messages,
        users,
        loading,
        presence,
        typingUsers,
        error,
        isTyping,
        messageStatuses,
        updateMessageStatus,
        getMessageStatus,
        sendMessage,
        createChannel,
        addUserToChannel,
        removeUserFromChannel,
        deleteMessage,
        editMessage,
        setTyping,
        markChannelAsRead,
        getUnreadCount,
        getUserPresence,
        getUser,
        getUsersWithPresence,
        getOnlineUsers,
        isUserTyping,
        getTypingUsers,
        getTypingText,
        addReaction,
        removeReaction,
        chatNotifications,
        chatUnreadCount,
        markChatNotificationRead,
        markChannelNotificationsRead,
        markAllChatNotificationsRead,
        clearAllChatNotifications,
        getChannelUnreadCount,
        notifyNewMessage,
        markMessagesAsRead: markMessagesAsReadManually
    };

    return (
        <ChatContext.Provider value={value}>
            {children}
        </ChatContext.Provider>
    );
};

export default ChatProvider;